import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MagnifyingGlass, User, EnvelopeSimple, Phone, CircleNotch, WarningOctagon, ShieldCheck,
  Sparkle, ArrowSquareOut, MapPin, DeviceMobile, ClockCounterClockwise, Trash, LinkSimple, Detective,
  DownloadSimple, IdentificationCard, Warning, ListChecks, At, Fingerprint, Globe,
  GithubLogo, CalendarStar, Buildings, Users, TreeStructure, Code, Star, ClockClockwise, Image as ImageIcon,
} from "@phosphor-icons/react";

const TYPE_META = {
  username: { icon: User, label: "Логин" },
  email: { icon: EnvelopeSimple, label: "Email" },
  phone: { icon: Phone, label: "Телефон" },
};

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="glass-card p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={17} weight="duotone" className="text-primary" />
        <h3 className="font-heading font-semibold text-sm">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function OsintPage() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [history, setHistory] = useState([]);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  const loadHistory = () => api.get("/osint/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const run = async (query = q, t = type) => {
    if (!query.trim()) return;
    setLoading(true);
    setRes(null);
    try {
      const { data } = await api.post("/osint/lookup", { query: query.trim(), type: t });
      setRes(data);
      loadHistory();
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };

  const exportPng = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(reportRef.current, { backgroundColor: "#0a0d16", pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.download = `osint-${res.query.replace(/[^a-z0-9]/gi, "_")}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Отчёт сохранён картинкой");
    } catch { toast.error("Не удалось создать картинку"); }
    setExporting(false);
  };

  const delHistory = async (id) => {
    await api.delete(`/osint/history/${id}`);
    loadHistory();
  };

  const scan = res?.username_scan;
  const catColors = { "Разработка": "#4dabf5", "Мессенджеры": "#0088cc", "Медиа": "#ff5500",
    "Игры": "#a970ff", "Сообщества": "#ff9100", "Персональные": "#00c9a7" };

  const dorks = res ? (() => {
    const t = encodeURIComponent(`"${res.query}"`);
    const names = (res.linked?.names || []).filter((n) => !n.startsWith("@"));
    const list = [
      { label: `Точное совпадение "${res.query}"`, g: `https://www.google.com/search?q=${t}`, y: `https://yandex.ru/search/?text=${t}` },
      { label: "Утёкшие документы (pdf/doc/xls)", g: `https://www.google.com/search?q=${t}+(filetype:pdf+OR+filetype:doc+OR+filetype:xls)`, y: `https://yandex.ru/search/?text=${t}+mime:pdf` },
      { label: "Резюме / профили", g: `https://www.google.com/search?q=${t}+(резюме+OR+CV+OR+профиль+OR+vk.com+OR+linkedin)`, y: `https://yandex.ru/search/?text=${t}+резюме` },
      { label: "Изображения", g: `https://www.google.com/search?tbm=isch&q=${t}`, y: `https://yandex.ru/images/search?text=${t}` },
    ];
    names.forEach((n) => {
      const nn = encodeURIComponent(`"${n}"`);
      list.push({ label: `Имя «${n}»`, g: `https://www.google.com/search?q=${nn}`, y: `https://yandex.ru/search/?text=${nn}` });
    });
    return list;
  })() : [];


  return (
    <div className="fade-in max-w-4xl" data-testid="osint-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5">
          <Detective size={13} weight="fill" className="text-primary" /> Поиск по открытым источникам
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Разведка</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Введите логин, email или телефон — соберём цифровой след из открытых источников: профили в соцсетях и сервисах,
          Gravatar, публичные утечки, метаданные номера. Всё легально, только открытые данные.
        </p>
      </div>

      <div className="glass-card p-4 mb-5">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder="username, email@example.com или +79261234567" className="pl-9" data-testid="osint-input" />
          </div>
          <Button onClick={() => run()} disabled={loading} data-testid="osint-search-button" className="shrink-0">
            {loading ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <MagnifyingGlass size={16} className="mr-1.5" />}
            {loading ? "Ищем…" : "Проверить"}
          </Button>
        </div>
        <div className="flex gap-1.5 mt-2.5">
          {[["auto", "Авто"], ["username", "Логин"], ["email", "Email"], ["phone", "Телефон"]].map(([id, label]) => (
            <button key={id} onClick={() => setType(id)} data-testid={`osint-type-${id}`}
              className={`text-xs px-2.5 py-1 rounded-full transition-colors ${type === id ? "bg-primary text-white font-semibold" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="glass-card p-10 text-center text-sm text-muted-foreground" data-testid="osint-loading">
          <CircleNotch size={28} className="animate-spin text-primary mx-auto mb-3" />
          Сканируем открытые источники… это занимает 10–20 секунд
        </div>
      )}

      {res && !loading && (
        <div className="space-y-4" data-testid="osint-results">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            {(() => { const M = TYPE_META[res.type]; return M ? <><M.icon size={16} className="text-primary" /><span className="text-muted-foreground">Тип:</span> <span className="font-semibold">{M.label}</span></> : null; })()}
            <span className="text-muted-foreground">·</span>
            <span className="font-mono num">{res.query}</span>
            <Button onClick={exportPng} disabled={exporting} size="sm" variant="secondary" data-testid="osint-export-button" className="ml-auto">
              {exporting ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : <DownloadSimple size={14} className="mr-1.5" />}
              {exporting ? "Готовим…" : "Сохранить отчёт (PNG)"}
            </Button>
          </div>

          <div ref={reportRef} className="space-y-4 rounded-2xl" style={{ background: "#0a0d16" }}>

          {res.ai_portrait && (
            <div className="glass-card p-4 sm:p-5 border-primary/30" data-testid="osint-ai">
              <div className="flex items-center gap-2 mb-2.5">
                <Sparkle size={16} weight="fill" className="text-primary" />
                <h3 className="font-heading font-semibold text-sm">Цифровой портрет (AI)</h3>
                {res.ai_portrait.confidence && (
                  <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    res.ai_portrait.confidence === "высокая" ? "bg-[#00e676]/15 text-[#00e676]" :
                    res.ai_portrait.confidence === "низкая" ? "bg-[#ff9100]/15 text-[#ff9100]" : "bg-primary/15 text-primary"}`}>
                    связность: {res.ai_portrait.confidence}
                  </span>
                )}
              </div>
              {res.ai_portrait.summary && <p className="text-sm leading-relaxed text-muted-foreground mb-3">{res.ai_portrait.summary}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[["identity", "Личность", IdentificationCard, "#4dabf5"], ["activity", "Активность", Fingerprint, "#00c9a7"],
                  ["risks", "Риски", Warning, "#ff3b30"], ["recommendations", "Проверить вручную", ListChecks, "#ff9100"]]
                  .filter(([k]) => (res.ai_portrait[k] || []).length).map(([k, label, Icon, color]) => (
                  <div key={k} className="rounded-xl bg-secondary/40 p-3">
                    <div className="flex items-center gap-1.5 text-xs font-semibold mb-1.5" style={{ color }}>
                      <Icon size={14} weight="duotone" /> {label}
                    </div>
                    <ul className="space-y-1">
                      {res.ai_portrait[k].map((x, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex gap-1.5"><span style={{ color }}>•</span>{x}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {res.github && (
            <Section title="GitHub — глубокий профиль" icon={GithubLogo} testid="osint-github">
              <div className="flex items-start gap-3 mb-3">
                {res.github.avatar && <img src={res.github.avatar} alt="" className="w-14 h-14 rounded-xl border border-border" />}
                <div className="min-w-0">
                  {res.github.name && <div className="font-semibold text-base">{res.github.name}</div>}
                  {res.github.bio && <div className="text-xs text-muted-foreground">{res.github.bio}</div>}
                  <a href={res.github.html_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5">{res.github.html_url?.replace("https://", "")} <ArrowSquareOut size={11} /></a>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[[Buildings, "Компания", res.github.company], [MapPin, "Локация", res.github.location],
                  [At, "Email", res.github.email], [At, "Twitter", res.github.twitter && "@" + res.github.twitter],
                  [Users, "Подписчиков", res.github.followers], [ListChecks, "Репозиториев", res.github.public_repos],
                  [CalendarStar, "На GitHub с", res.github.created_at ? res.github.created_at.slice(0, 10) : null]]
                  .filter(([, , v]) => v != null && v !== "").map(([Icon, l, v]) => (
                  <div key={l} className="rounded-lg bg-secondary/60 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Icon size={11} /> {l}</div>
                    <div className="text-sm font-semibold break-words num">{v}</div>
                  </div>
                ))}
              </div>
              {res.github.blog && <a href={res.github.blog.startsWith("http") ? res.github.blog : `https://${res.github.blog}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 mt-2"><Globe size={12} /> {res.github.blog}</a>}
              {res.github.languages?.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
                  <Code size={13} className="text-muted-foreground" />
                  {res.github.languages.map((l) => <span key={l} className="text-[11px] px-2 py-0.5 rounded-full bg-[#4dabf5]/15 text-[#4dabf5]">{l}</span>)}
                </div>
              )}
              {res.github.orgs?.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap mt-2">
                  <Buildings size={13} className="text-muted-foreground" />
                  {res.github.orgs.map((o) => <a key={o} href={`https://github.com/${o}`} target="_blank" rel="noreferrer" className="text-[11px] px-2 py-0.5 rounded-full bg-secondary hover:text-primary transition-colors">{o}</a>)}
                </div>
              )}
              {res.github.top_repos?.length > 0 && (
                <div className="mt-2.5 space-y-1">
                  {res.github.top_repos.map((r) => (
                    <a key={r.name} href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-secondary/40 transition-colors">
                      <Code size={12} className="text-muted-foreground shrink-0" />
                      <span className="font-medium truncate">{r.name}</span>
                      <span className="flex items-center gap-0.5 num text-muted-foreground ml-auto shrink-0"><Star size={11} weight="fill" className="text-[#f5a623]" /> {r.stars}</span>
                    </a>
                  ))}
                </div>
              )}
              {res.github.socials?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {res.github.socials.map((s, i) => <a key={i} href={s} target="_blank" rel="noreferrer" className="text-xs px-2 py-0.5 rounded-full bg-secondary hover:text-primary transition-colors">{s.replace(/https?:\/\//, "").slice(0, 30)}</a>)}
                </div>
              )}
            </Section>
          )}

          {res.timeline && (
            <Section title="Таймлайн цифрового следа" icon={CalendarStar} testid="osint-timeline">
              <p className="text-sm mb-3">Цифровому следу примерно <span className="font-bold num text-primary">{res.timeline.footprint_years} лет</span> · первое появление {new Date(res.timeline.first_seen).toLocaleDateString("ru-RU", { month: "long", year: "numeric" })}</p>
              <div className="relative pl-4">
                {res.timeline.events.map((e, i) => (
                  <div key={i} className="flex items-center gap-3 pb-3 relative">
                    <div className="absolute -left-4 top-1 w-2 h-2 rounded-full bg-primary" />
                    {i < res.timeline.events.length - 1 && <div className="absolute -left-[13px] top-3 w-px h-full bg-border/60" />}
                    <span className="text-xs num text-muted-foreground w-20 shrink-0">{e.date}</span>
                    <span className="text-sm font-medium">{e.service}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {res.wayback?.length > 0 && (
            <Section title="История (Web Archive)" icon={ClockClockwise} testid="osint-wayback">
              <p className="text-[11px] text-muted-foreground mb-2">Архивные снимки профилей — старые версии и удалённые данные.</p>
              <div className="space-y-1.5">
                {res.wayback.map((w, i) => (
                  <a key={i} href={w.snapshot} target="_blank" rel="noreferrer" data-testid={`osint-wayback-${i}`}
                    className="flex items-center gap-2 rounded-lg bg-secondary/40 px-3 py-2 hover:bg-secondary transition-colors">
                    <ClockClockwise size={14} className="text-primary shrink-0" />
                    <span className="text-xs truncate flex-1 min-w-0">{w.url.replace(/https?:\/\//, "")}</span>
                    <span className="text-[11px] num text-muted-foreground shrink-0">с {w.first}</span>
                    <ArrowSquareOut size={12} className="text-muted-foreground shrink-0" />
                  </a>
                ))}
              </div>
            </Section>
          )}

          {res.linked?.avatars?.length > 0 && (
            <Section title="Реверс-поиск по фото профиля" icon={ImageIcon} testid="osint-reverse">
              <p className="text-[11px] text-muted-foreground mb-3">Найти те же фото на других сайтах — часто вычисляет скрытые аккаунты.</p>
              <div className="flex flex-wrap gap-3">
                {res.linked.avatars.map((a, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5" data-testid={`osint-reverse-${i}`}>
                    <img src={a} alt="" className="w-16 h-16 rounded-xl border border-border object-cover" />
                    <div className="flex gap-1">
                      <a href={`https://lens.google.com/uploadbyurl?url=${encodeURIComponent(a)}`} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 rounded bg-secondary hover:bg-primary hover:text-white transition-colors">Google</a>
                      <a href={`https://yandex.ru/images/search?rpt=imageview&url=${encodeURIComponent(a)}`} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 rounded bg-secondary hover:bg-primary hover:text-white transition-colors">Yandex</a>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {res.linked && (res.linked.profiles.length > 0 || res.linked.names.length > 0) && (
            <Section title="Связанные идентичности" icon={TreeStructure} testid="osint-linked">
              {res.linked.names.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {res.linked.avatars.map((a, i) => <img key={i} src={a} alt="" className="w-8 h-8 rounded-full border border-border" />)}
                  {res.linked.names.map((n, i) => <span key={i} className="text-sm font-semibold px-2 py-0.5 rounded-md bg-secondary">{n}</span>)}
                </div>
              )}
              <p className="text-xs text-muted-foreground">Один и тот же идентификатор связывает {res.linked.profiles.length} публичных профилей — сведены в единый след.</p>
            </Section>
          )}

          {res.phone && (
            <Section title="Метаданные номера" icon={DeviceMobile} testid="osint-phone">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                {[["Формат", res.phone.international], ["Страна", res.phone.country], ["Оператор", res.phone.carrier],
                  ["Тип линии", res.phone.line_type], ["Регион", res.phone.region_code],
                  ["Валиден", res.phone.valid ? "Да" : "Нет"]].filter(([, v]) => v).map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-secondary/60 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
                    <div className="text-sm font-semibold">{v}</div>
                  </div>
                ))}
              </div>
              {res.phone.timezones?.length > 0 && (
                <p className="text-xs text-muted-foreground mb-3 flex items-start gap-1.5"><MapPin size={13} className="mt-0.5 shrink-0" /> Часовые пояса: {res.phone.timezones.slice(0, 4).join(", ")}{res.phone.timezones.length > 4 ? "…" : ""}</p>
              )}
              <div className="flex flex-wrap gap-2">
                {res.phone.messengers.map((m) => (
                  <a key={m.name} href={m.url} target="_blank" rel="noreferrer" data-testid={`osint-messenger-${m.name}`}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-secondary hover:bg-primary hover:text-white transition-colors">
                    <LinkSimple size={12} /> {m.name}
                  </a>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Ссылки открывают чат, если номер зарегистрирован в мессенджере.</p>
            </Section>
          )}

          {res.domain && (
            <Section title="Анализ домена email" icon={Globe} testid="osint-domain">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                {[["Домен", res.domain.domain], ["Тип", res.domain.type], ["Почтовый провайдер", res.domain.provider]]
                  .filter(([, v]) => v).map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-secondary/60 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
                    <div className="text-sm font-semibold break-words">{v}</div>
                  </div>
                ))}
              </div>
              {res.domain.disposable && (
                <p className="text-xs text-[#ff9100] flex items-center gap-1.5 mb-1"><Warning size={13} weight="fill" /> Одноразовый (временный) email-домен</p>
              )}
              {res.domain.mx?.length > 0 && (
                <p className="text-[11px] text-muted-foreground">MX-серверы: {res.domain.mx.join(", ")}</p>
              )}
              {res.domain.whois && (
                <div className="mt-3 pt-3 border-t border-border/40">
                  <div className="text-xs font-semibold mb-1.5">WHOIS домена</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[["Регистратор", res.domain.whois.registrar], ["Владелец", res.domain.whois.org],
                      ["Создан", res.domain.whois.created], ["Истекает", res.domain.whois.expires]]
                      .filter(([, v]) => v).map(([l, v]) => (
                      <div key={l} className="rounded-lg bg-secondary/60 px-2.5 py-1.5">
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{l}</div>
                        <div className="text-xs font-semibold break-words num">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          )}

          {res.gravatar && (
            <Section title="Gravatar" icon={User} testid="osint-gravatar">
              <div className="flex items-center gap-3">
                {res.gravatar.thumbnail && <img src={res.gravatar.thumbnail} alt="" className="w-14 h-14 rounded-full border border-border" />}
                <div className="min-w-0">
                  {res.gravatar.display_name && <div className="font-semibold">{res.gravatar.display_name}</div>}
                  {res.gravatar.username && <div className="text-xs text-muted-foreground">@{res.gravatar.username}</div>}
                  {res.gravatar.location && <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={11} /> {res.gravatar.location}</div>}
                  {res.gravatar.profile_url && <a href={res.gravatar.profile_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5">Профиль <ArrowSquareOut size={11} /></a>}
                </div>
              </div>
              {res.gravatar.accounts?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {res.gravatar.accounts.map((a, i) => (
                    <a key={i} href={a.url} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1 rounded-full bg-secondary hover:text-primary transition-colors">{a.name}</a>
                  ))}
                </div>
              )}
            </Section>
          )}

          {scan && (
            <Section title={`Профили по логину «${res.scanned_handle}»`} icon={User} testid="osint-profiles">
              <p className="text-xs text-muted-foreground mb-3">
                Найдено <span className="font-semibold text-foreground num">{scan.found.length}</span> из {scan.checked} проверенных сервисов
              </p>
              {scan.found.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                  {scan.found.map((f, i) => (
                    <a key={i} href={f.url} target="_blank" rel="noreferrer" data-testid={`osint-found-${i}`}
                      className="flex items-center gap-2.5 rounded-lg border border-border/60 px-3 py-2 hover:border-primary/60 transition-colors">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: catColors[f.cat] || "#888" }} />
                      <span className="text-sm font-medium">{f.name}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{f.cat}</span>
                      <ArrowSquareOut size={13} className="text-muted-foreground" />
                    </a>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground mb-4">Проверенные сервисы не дали совпадений.</p>}

              <div className="text-xs text-muted-foreground mb-2">Соцсети — проверьте вручную (блокируют автопроверку):</div>
              <div className="flex flex-wrap gap-1.5">
                {scan.manual_links.map((l) => (
                  <a key={l.name} href={l.url} target="_blank" rel="noreferrer" data-testid={`osint-manual-${l.name}`}
                    className="text-xs px-2.5 py-1 rounded-full bg-secondary hover:text-primary transition-colors flex items-center gap-1">
                    {l.name} <ArrowSquareOut size={10} />
                  </a>
                ))}
              </div>
            </Section>
          )}

          {res.profiles_extra?.length > 0 && (
            <Section title="Личные данные из открытых профилей" icon={IdentificationCard} testid="osint-personal">
              <p className="text-[11px] text-muted-foreground mb-2">Указано самими пользователями в публичных профилях.</p>
              <div className="space-y-2">
                {res.profiles_extra.map((p, i) => (
                  <div key={i} className="rounded-lg border border-border/60 px-3 py-2" data-testid={`osint-personal-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-semibold">{p.source}</span>
                      {p.name && <span className="text-sm font-semibold">{p.name}</span>}
                      {p.location && <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={11} /> {p.location}</span>}
                    </div>
                    {p.bio && <p className="text-xs text-muted-foreground mt-1">{p.bio}</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {dorks.length > 0 && (
            <Section title="Расширенный поиск (Google / Yandex)" icon={MagnifyingGlass} testid="osint-dorks">
              <p className="text-[11px] text-muted-foreground mb-3">Готовые поисковые запросы — открывают публичные упоминания в один клик.</p>
              <div className="space-y-2">
                {dorks.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg bg-secondary/40 px-3 py-2" data-testid={`osint-dork-${i}`}>
                    <span className="text-xs flex-1 min-w-0 truncate">{d.label}</span>
                    <a href={d.g} target="_blank" rel="noreferrer" className="text-[11px] px-2 py-1 rounded bg-secondary hover:bg-primary hover:text-white transition-colors shrink-0">Google</a>
                    <a href={d.y} target="_blank" rel="noreferrer" className="text-[11px] px-2 py-1 rounded bg-secondary hover:bg-primary hover:text-white transition-colors shrink-0">Yandex</a>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title="Публичные утечки" icon={res.breaches?.found ? WarningOctagon : ShieldCheck} testid="osint-breaches">
            {!res.breaches ? (
              <p className="text-sm text-muted-foreground">Источник утечек недоступен для этого запроса.</p>
            ) : res.breaches.found ? (
              <>
                <p className="text-sm mb-3">
                  <span className="text-[#ff3b30] font-bold num">{res.breaches.found}</span> совпадений в публичных базах утечек.
                  {res.breaches.sources.length > 0 && " Источники:"}
                </p>
                {res.breaches.sources.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {res.breaches.sources.map((s, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded-md bg-[#ff3b30]/10 text-[#ff6b60] border border-[#ff3b30]/20">
                        {s.name}{s.date ? ` · ${s.date}` : ""}
                      </span>
                    ))}
                  </div>
                )}
                {res.breaches.fields?.length > 0 && (
                  <p className="text-[11px] text-muted-foreground">Засветившиеся типы данных: {res.breaches.fields.slice(0, 20).join(", ")}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-[#00e676] flex items-center gap-1.5"><ShieldCheck size={16} weight="fill" /> В публичных базах утечек совпадений не найдено.</p>
            )}
          </Section>
          </div>
        </div>
      )}

      {history.length > 0 && !loading && (
        <div className="glass-card p-4 mt-5" data-testid="osint-history">
          <div className="flex items-center gap-2 mb-3">
            <ClockCounterClockwise size={16} className="text-muted-foreground" />
            <h3 className="font-heading font-semibold text-sm">История поиска</h3>
            <button onClick={() => delHistory("all")} data-testid="osint-history-clear" className="ml-auto text-xs text-muted-foreground hover:text-destructive transition-colors">Очистить</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-secondary group" data-testid={`osint-history-${h.id}`}>
                <button onClick={() => { setQ(h.query); setType(h.type); run(h.query, h.type); }} className="hover:text-primary transition-colors font-mono">{h.query}</button>
                <button onClick={() => delHistory(h.id)} className="text-muted-foreground/50 group-hover:text-destructive transition-colors"><Trash size={11} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
